import {
  addTranscription,
  formatTime,
  startTranscribing,
  stopTranscribing,
} from "./helpers";
import {
  LocalUser,
  RemoteUser,
  useJoin,
  useLocalCameraTrack,
  useLocalMicrophoneTrack,
  usePublish,
  useRemoteAudioTracks,
  useRemoteUsers,
  useRTCClient,
} from "agora-rtc-react";
import { useEffect, useState, useRef, useMemo } from "react";
import protoRoot from "./parseSTT";

const styles = {
  modalContainer: {
    backgroundColor: "#fff",
    borderRadius: "20px",
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    maxWidth: "396px",
    padding: "16px 40px 24px 40px",
    textAlign: "center",
  },

  modalOverlay: {
    alignItems: "center",
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    bottom: 0,
    display: "flex",
    justifyContent: "center",
    left: 0,
    position: "fixed",
    right: 0,
    top: 0,
    zIndex: 3,
  },
};

const VideoCall = ({
  appId,
  appointment,
  channelName,
  devicePreferences,
  handleClose,
  isJoin,
  token,
  uid,
  user,
  provider,
}) => {
  const [cameraOn, setCamera] = useState(devicePreferences?.cameraOn ?? false);
  const [duration, setDuration] = useState(0);
  const [isCallActive, setIsCallActive] = useState(false);
  const [micOn, setMic] = useState(devicePreferences?.micOn ?? false);
  const [showEndModal, setShowEndModal] = useState(false);
  const [timerId, setTimerId] = useState(null);
  const [transcript, setTranscript] = useState([]);
  const [transcriptionData, setTranscriptionData] = useState({
    builderToken: "",
    taskId: "",
  });
  const [transcriptionOn, setTranscriptionOn] = useState(false);

  const cameraTrackConfig = useMemo(
    () =>
      devicePreferences?.cameraId
        ? { cameraId: devicePreferences.cameraId }
        : undefined,
    [devicePreferences?.cameraId]
  );

  const micTrackConfig = useMemo(
    () =>
      devicePreferences?.micId
        ? { microphoneId: devicePreferences.micId }
        : undefined,
    [devicePreferences?.micId]
  );

  const { localCameraTrack } = useLocalCameraTrack(cameraOn, cameraTrackConfig);
  const { localMicrophoneTrack } = useLocalMicrophoneTrack(micOn, micTrackConfig);

  const tracksToPublish = useMemo(
    () => [
      micOn ? localMicrophoneTrack : null,
      cameraOn ? localCameraTrack : null,
    ],
    [cameraOn, localCameraTrack, localMicrophoneTrack, micOn]
  );

  const client = useRTCClient();
  const remoteUsers = useRemoteUsers();
  const transcriptRef = useRef(null);

  const { audioTracks } = useRemoteAudioTracks(remoteUsers);
  audioTracks[0]?.play();

  const realRemoteUsers = useMemo(
    () => remoteUsers.filter((u) => u.uid !== 101),
    [remoteUsers.length]
  );

  useJoin(
    {
      appid: appId,
      channel: channelName,
      token: token ?? null,
      uid: uid ?? null,
    },
    isJoin
  );
  usePublish(tracksToPublish);

  useEffect(() => {
    if (!localCameraTrack) return undefined;

    localCameraTrack
      .setEnabled(cameraOn)
      .catch((error) => console.warn("Unable to toggle camera track:", error));

    if (!cameraOn) {
      localCameraTrack.stop();
    }

    return undefined;
  }, [cameraOn, localCameraTrack]);

  useEffect(() => {
    if (!localMicrophoneTrack) return undefined;

    localMicrophoneTrack
      .setEnabled(micOn)
      .catch((error) => console.warn("Unable to toggle microphone track:", error));

    return undefined;
  }, [micOn, localMicrophoneTrack]);

  useEffect(() => {
    if (!localCameraTrack || !devicePreferences?.cameraId) return;

    localCameraTrack
      .setDevice(devicePreferences.cameraId)
      .catch((error) => console.warn("Unable to set camera device:", error));
  }, [devicePreferences?.cameraId, localCameraTrack]);

  useEffect(() => {
    if (!localMicrophoneTrack || !devicePreferences?.micId) return;

    localMicrophoneTrack
      .setDevice(devicePreferences.micId)
      .catch((error) => console.warn("Unable to set microphone device:", error));
  }, [devicePreferences?.micId, localMicrophoneTrack]);

  useEffect(() => {
    if (!devicePreferences?.speakerId || !audioTracks.length) return;

    audioTracks.forEach((track) => {
      track
        .setPlaybackDevice(devicePreferences.speakerId)
        .catch((error) => console.warn("Unable to set speaker device:", error));
    });
  }, [audioTracks, devicePreferences?.speakerId]);

  const endCall = () => {
    stopTranscribing(transcriptionData.builderToken, transcriptionData.taskId);

    localStorage.removeItem("activeCallSession");

    handleClose();
  };

  useEffect(() => {
    const init = async () => {
      if (channelName && token && uid) {
        startTranscribing(
          channelName,
          setTranscriptionData,
          setTranscriptionOn,
          token,
          uid
        );
      }

      if (client) {
        client.on("stream-message", (uid, stream) => {
          const streamData = protoRoot.Agora.SpeechToText.Text.decode(stream);

          if (streamData.dataType === "translate") {
            if (
              streamData?.trans[0]?.lang === "en-US" &&
              streamData?.trans[0]?.isFinal
            ) {
              setTranscript((prevTranscript) => [
                ...prevTranscript,
                {
                  isRemote: streamData.uid !== uid ? true : false,
                  message: streamData.trans[0].texts.join(""),
                },
              ]);
            }
          }
        });
      }
    };

    init();

    return () => {
      if (client) client.removeAllListeners();
    };
  }, [client]);

  useEffect(() => {
    if (isCallActive) {
      const startTime = Date.now();

      const id = setInterval(() => {
        const timeElapsed = Math.floor((Date.now() - startTime) / 1000);

        setDuration(timeElapsed);
      }, 1000);

      setTimerId(id);
    } else {
      clearInterval(timerId);
      setDuration(0);

      localStorage.removeItem("activeCallSession");
    }

    return () => {
      clearInterval(timerId);
    };
  }, [isCallActive]);

  useEffect(() => {
    if (realRemoteUsers.length > 0) {
      setIsCallActive(true);

      if (!cameraOn) {
        setCamera(true);
      }

      if (!micOn) {
        setMic(true);
      }

      const callSessionData = {
        appointmentId: appointment?._id,
        channelName: channelName,
        startTime: new Date().toISOString(),
      };

      localStorage.setItem(
        "activeCallSession",
        JSON.stringify(callSessionData)
      );
    }
  }, [realRemoteUsers.length, appointment?._id, channelName, cameraOn, micOn]);

  return (
    <div className="video-call-container">
      {realRemoteUsers.length === 0 && (
        <div className="WaitignModal" style={styles.modalOverlay}>
          <div style={styles.modalContainer}>
            <p className="ModalHeading" style={styles.modalHeading}>
              Waiting for Client
            </p>

            <p className="ModalText" style={styles.modalText}>
              The client has not joined the call yet. You can start your camera
              and microphone.
            </p>

            <button
              className="ModalBtn"
              onClick={endCall}
              style={{ marginTop: "10px" }}>
              Back
            </button>
          </div>
        </div>
      )}

      {showEndModal && (
        <div className="WaitignModal" style={styles.modalOverlay}>
          <div className="EndCallContainer">
            <p className="ModalHeading">End Call</p>

            <p className="ModalText">Are you sure you want to end the call?</p>

            <button
              className="ModalBtn"
              onClick={() => {
                addTranscription(
                  appointment._id,
                  transcript,
                  appointment.specialistid,
                  appointment.user_fk
                );
                endCall();
              }}>
              Yes
            </button>

            <button className="ModalBtn" onClick={() => setShowEndModal(false)}>
              No
            </button>
          </div>
        </div>
      )}

      <div className="VideoBox">
        <div className="VideCallBoxOuter">
          <div className="VideCallBox">
            {realRemoteUsers.length > 0 ? (
              <RemoteUser className="RemoteUserBox" user={realRemoteUsers[0]} />
            ) : (
              <svg
                fill="none"
                height="116"
                viewBox="0 0 116 116"
                width="116"
                xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M77.3333 77.3333V82.1666C77.3333 84.7304 76.3148 87.1891 74.502 89.002C72.6891 90.8148 70.2303 91.8333 67.6666 91.8333H14.4999C11.9362 91.8333 9.4774 90.8148 7.66455 89.002C5.8517 87.1891 4.83325 84.7304 4.83325 82.1666V33.8333C4.83325 31.2695 5.8517 28.8108 7.66455 26.9979C9.4774 25.1851 11.9362 24.1666 14.4999 24.1666H24.1666M51.5233 24.1666H67.6666C70.2303 24.1666 72.6891 25.1851 74.502 26.9979C76.3148 28.8108 77.3333 31.2695 77.3333 33.8333V49.9766L82.1666 54.81L111.167 33.8333V82.1666"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="5"
                  stroke="#C2C2C2"
                />

                <path
                  d="M4.83325 4.83337L111.167 111.167"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="5"
                  stroke="#C2C2C2"
                />
              </svg>
            )}
          </div>

          <div className="userName">
            <p>
              {user?.firstname + " " + user?.lastname ?? user?.email ?? "User"}
            </p>
          </div>

          {realRemoteUsers.length > 0 && (
            <LocalUser
              key={localCameraTrack?.getTrackId?.() ?? "local-user"}
              audioTrack={localMicrophoneTrack}
              cameraOn={cameraOn}
              className="LocalUserBox"
              micOn={micOn}
              playAudio={false}
              playVideo={cameraOn}
              videoTrack={localCameraTrack}
            />
          )}
        </div>

        <div className="videoControlsBox">
          <div className="videoControlsStart">
            <div className="videoControlsBtnT">
              <svg
                fill="none"
                height="27"
                viewBox="0 0 23 27"
                width="23"
                xmlns="http://www.w3.org/2000/svg">
                <ellipse
                  cx="11.3171"
                  cy="13.2079"
                  fill-opacity="0.63"
                  fill="white"
                  rx="10.841"
                  ry="13.1437"
                />

                <ellipse
                  cx="11.3172"
                  cy="13.2086"
                  fill="#EB5757"
                  rx="4.11212"
                  ry="4.98555"
                />
              </svg>

              <span style={{ fontVariantNumeric: "tabular-nums" }}>
                {formatTime(duration)}
              </span>
            </div>
          </div>
          <div className="audio-video-controls">
            <button
              className={`videoControlsBtn ${cameraOn && "active"}`}
              onClick={() => setCamera((pre) => !pre)}>
              {cameraOn ? (
                <svg
                  fill="none"
                  height="31"
                  viewBox="0 0 24 24"
                  width="31"
                  xmlns="http://www.w3.org/2000/svg">
                  <path
                    clip-rule="evenodd"
                    d="M0 7.5C0 6.70435 0.316071 5.94129 0.87868 5.37868C1.44129 4.81607 2.20435 4.5 3 4.5H14.25C14.9776 4.49991 15.6805 4.76429 16.2277 5.24389C16.7749 5.72349 17.1292 6.38563 17.2245 7.107L21.8895 5.034C22.1178 4.93226 22.368 4.8892 22.6172 4.90873C22.8664 4.92826 23.1068 5.00977 23.3165 5.14584C23.5262 5.28192 23.6986 5.46824 23.8179 5.68789C23.9373 5.90753 23.9999 6.15352 24 6.4035V17.5965C23.9998 17.8463 23.9372 18.0921 23.8179 18.3115C23.6986 18.531 23.5264 18.7172 23.3169 18.8532C23.1074 18.9893 22.8673 19.0708 22.6183 19.0905C22.3693 19.1102 22.1193 19.0674 21.891 18.966L17.2245 16.893C17.1292 17.6144 16.7749 18.2765 16.2277 18.7561C15.6805 19.2357 14.9776 19.5001 14.25 19.5H3C2.20435 19.5 1.44129 19.1839 0.87868 18.6213C0.316071 18.0587 0 17.2956 0 16.5V7.5ZM17.25 15.2625L22.5 17.5965V6.4035L17.25 8.7375V15.2625ZM3 6C2.60218 6 2.22064 6.15804 1.93934 6.43934C1.65804 6.72064 1.5 7.10218 1.5 7.5V16.5C1.5 16.8978 1.65804 17.2794 1.93934 17.5607C2.22064 17.842 2.60218 18 3 18H14.25C14.6478 18 15.0294 17.842 15.3107 17.5607C15.592 17.2794 15.75 16.8978 15.75 16.5V7.5C15.75 7.10218 15.592 6.72064 15.3107 6.43934C15.0294 6.15804 14.6478 6 14.25 6H3Z"
                    fill-rule="evenodd"
                    fill="#27B99A"
                  />
                </svg>
              ) : (
                <svg
                  fill="none"
                  height="31"
                  viewBox="0 0 31 31"
                  width="31"
                  xmlns="http://www.w3.org/2000/svg">
                  <path
                    clip-rule="evenodd"
                    d="M21.2369 23.9572C21.7895 23.3683 22.1433 22.6208 22.2483 21.8201L28.2739 24.4977C28.5689 24.6292 28.892 24.6848 29.2139 24.6595C29.5358 24.6343 29.8463 24.529 30.1172 24.3533C30.388 24.1775 30.6107 23.9368 30.7648 23.6531C30.919 23.3694 30.9999 23.0517 31 22.7288V8.27117C30.9997 7.94853 30.9189 7.63107 30.7648 7.3476C30.6107 7.06413 30.3883 6.82362 30.1177 6.6479C29.8471 6.47219 29.5369 6.36683 29.2153 6.3414C28.8936 6.31596 28.5707 6.37124 28.2759 6.50224L22.2483 9.17986C22.1252 8.24809 21.6676 7.39283 20.9608 6.77335C20.254 6.15386 19.3461 5.81237 18.4062 5.81249H8.277L9.66038 7.74999H18.4062C18.9201 7.74999 19.4129 7.95411 19.7763 8.31747C20.1396 8.68082 20.3438 9.17363 20.3438 9.68749V21.3125C20.3441 21.6662 20.2476 22.0133 20.0648 22.3161L21.2369 23.9572ZM2.76675 8.09873C2.5107 8.27709 2.30156 8.51472 2.15717 8.79135C2.01278 9.06799 1.93742 9.37543 1.9375 9.68749V21.3125C1.9375 21.8263 2.14163 22.3192 2.50498 22.6825C2.86833 23.0459 3.36114 23.25 3.875 23.25H13.5896L14.973 25.1875H3.875C2.84729 25.1875 1.86166 24.7792 1.13496 24.0525C0.408258 23.3258 0 22.3402 0 21.3125V9.68749C0 8.37967 0.647125 7.22299 1.64106 6.52161L2.76675 8.09873ZM29.0625 22.7269L22.2812 19.714V11.2859L29.0625 8.27117V22.7288V22.7269ZM20.5239 29.6244L1.14894 2.49936L2.72606 1.37561L22.1011 28.5006L20.5239 29.6244Z"
                    fill-rule="evenodd"
                    fill="white"
                  />
                </svg>
              )}
            </button>

            <button
              className={`videoControlsBtn ${micOn && "active"}`}
              onClick={() => setMic((pre) => !pre)}>
              {micOn ? (
                <svg
                  fill="none"
                  height="31"
                  viewBox="0 0 31 31"
                  width="31"
                  xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M6.78125 12.5938C7.03818 12.5938 7.28458 12.6958 7.46626 12.8775C7.64794 13.0592 7.75 13.3056 7.75 13.5625V15.5C7.75 17.5554 8.56652 19.5267 10.0199 20.9801C11.4733 22.4335 13.4446 23.25 15.5 23.25C17.5554 23.25 19.5267 22.4335 20.9801 20.9801C22.4335 19.5267 23.25 17.5554 23.25 15.5V13.5625C23.25 13.3056 23.3521 13.0592 23.5337 12.8775C23.7154 12.6958 23.9618 12.5938 24.2188 12.5938C24.4757 12.5938 24.7221 12.6958 24.9038 12.8775C25.0854 13.0592 25.1875 13.3056 25.1875 13.5625V15.5C25.1875 17.9016 24.2955 20.2176 22.6845 21.9986C21.0735 23.7797 18.8583 24.8989 16.4688 25.1391V29.0625H22.2812C22.5382 29.0625 22.7846 29.1646 22.9663 29.3462C23.1479 29.5279 23.25 29.7743 23.25 30.0312C23.25 30.2882 23.1479 30.5346 22.9663 30.7163C22.7846 30.8979 22.5382 31 22.2812 31H8.71875C8.46182 31 8.21542 30.8979 8.03374 30.7163C7.85206 30.5346 7.75 30.2882 7.75 30.0312C7.75 29.7743 7.85206 29.5279 8.03374 29.3462C8.21542 29.1646 8.46182 29.0625 8.71875 29.0625H14.5312V25.1391C12.1417 24.8989 9.92654 23.7797 8.31552 21.9986C6.70449 20.2176 5.81247 17.9016 5.8125 15.5V13.5625C5.8125 13.3056 5.91456 13.0592 6.09624 12.8775C6.27792 12.6958 6.52432 12.5938 6.78125 12.5938Z"
                    fill="#27B99A"
                  />

                  <path
                    d="M19.375 15.5C19.375 16.5277 18.9667 17.5133 18.24 18.24C17.5133 18.9667 16.5277 19.375 15.5 19.375C14.4723 19.375 13.4867 18.9667 12.76 18.24C12.0333 17.5133 11.625 16.5277 11.625 15.5V5.8125C11.625 4.78479 12.0333 3.79916 12.76 3.07246C13.4867 2.34576 14.4723 1.9375 15.5 1.9375C16.5277 1.9375 17.5133 2.34576 18.24 3.07246C18.9667 3.79916 19.375 4.78479 19.375 5.8125V15.5ZM15.5 0C13.9584 0 12.48 0.612387 11.3899 1.70244C10.2999 2.7925 9.6875 4.27093 9.6875 5.8125V15.5C9.6875 17.0416 10.2999 18.52 11.3899 19.6101C12.48 20.7001 13.9584 21.3125 15.5 21.3125C17.0416 21.3125 18.52 20.7001 19.6101 19.6101C20.7001 18.52 21.3125 17.0416 21.3125 15.5V5.8125C21.3125 4.27093 20.7001 2.7925 19.6101 1.70244C18.52 0.612387 17.0416 0 15.5 0Z"
                    fill="#27B99A"
                  />
                </svg>
              ) : (
                <svg
                  fill="none"
                  height="31"
                  viewBox="0 0 31 31"
                  width="31"
                  xmlns="http://www.w3.org/2000/svg">
                  <g clip-path="url(#clip0_12225_840)">
                    <path
                      d="M25.1875 15.5C25.1875 16.5927 25.0054 17.6448 24.6721 18.6251L23.095 17.048C23.1982 16.5385 23.2501 16.0199 23.25 15.5V13.5625C23.25 13.3055 23.3521 13.0591 23.5337 12.8774C23.7154 12.6958 23.9618 12.5937 24.2188 12.5937C24.4757 12.5937 24.7221 12.6958 24.9038 12.8774C25.0854 13.0591 25.1875 13.3055 25.1875 13.5625V15.5ZM15.5 23.25C17.0849 23.25 18.5574 22.7753 19.7857 21.9576L21.1769 23.3507C19.7938 24.3537 18.1689 24.9709 16.4688 25.139V29.0625H22.2812C22.5382 29.0625 22.7846 29.1645 22.9663 29.3462C23.1479 29.5279 23.25 29.7743 23.25 30.0312C23.25 30.2881 23.1479 30.5345 22.9663 30.7162C22.7846 30.8979 22.5382 31 22.2812 31H8.71875C8.46182 31 8.21542 30.8979 8.03374 30.7162C7.85206 30.5345 7.75 30.2881 7.75 30.0312C7.75 29.7743 7.85206 29.5279 8.03374 29.3462C8.21542 29.1645 8.46182 29.0625 8.71875 29.0625H14.5312V25.139C12.1417 24.8989 9.92654 23.7797 8.31552 21.9986C6.70449 20.2175 5.81247 17.9015 5.8125 15.5V13.5625C5.8125 13.3055 5.91456 13.0591 6.09624 12.8774C6.27792 12.6958 6.52432 12.5937 6.78125 12.5937C7.03818 12.5937 7.28458 12.6958 7.46626 12.8774C7.64794 13.0591 7.75 13.3055 7.75 13.5625V15.5C7.75 17.5554 8.56652 19.5266 10.0199 20.98C11.4733 22.4334 13.4446 23.25 15.5 23.25ZM21.3125 5.81245V15.2655L19.375 13.328V5.81245C19.3795 4.80158 18.9887 3.82898 18.2862 3.10215C17.5836 2.37532 16.6248 1.95182 15.6144 1.92199C14.6039 1.89216 13.6218 2.25836 12.8776 2.94248C12.1334 3.6266 11.686 4.57445 11.6308 5.58383L9.99363 3.94664C10.4372 2.64317 11.3289 1.53941 12.5101 0.831853C13.6913 0.124296 15.0852 -0.141138 16.4438 0.0828057C17.8023 0.306749 19.0373 1.00554 19.9289 2.05477C20.8205 3.104 21.3108 4.43558 21.3125 5.81245Z"
                      fill="white"
                    />

                    <path
                      d="M18.3795 20.551L16.9302 19.1018C16.3424 19.3351 15.7064 19.4211 15.0778 19.3521C14.4491 19.2832 13.8469 19.0614 13.3237 18.7061C12.8004 18.3509 12.3721 17.873 12.0761 17.3141C11.78 16.7552 11.6253 16.1324 11.6253 15.5V13.7988L9.68783 11.8613V15.5C9.68753 16.5178 9.95449 17.5178 10.462 18.4C10.9695 19.2823 11.6998 20.0158 12.5798 20.5272C13.4598 21.0386 14.4586 21.31 15.4764 21.3141C16.4942 21.3183 17.4953 21.0552 18.3795 20.551ZM3.18945 2.62334L26.4395 25.8733L27.8112 24.5016L4.5612 1.25159L3.18945 2.62334Z"
                      fill="white"
                    />
                  </g>

                  <defs>
                    <clipPath id="clip0_12225_840">
                      <rect fill="white" height="31" width="31" />
                    </clipPath>
                  </defs>
                </svg>
              )}
            </button>
          </div>

          <button
            className="videoControlsBtnD"
            onClick={() => {
              setShowEndModal(true);
            }}>
            End Call
          </button>
        </div>
      </div>
    </div>
  );
};

export default VideoCall;
